import classNames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import Constants from '@/constants/const';
import { CmsComponentProps, ImgSize, ISize, IStatus } from '@/types/cms';
import ImpTracker from '@/components/common/ImpTracker';
import useImpressionT2 from '@/tracks/useImpressionT2';
import useClickActionT2 from '@/tracks/useClickActionT2';
import { getCookie } from '@/utils/cookie';
import { getHomePageDataByUrl } from '@/api/content';
import Size from '../../types/size';
import { twMerge } from 'tailwind-merge';
import { checkIsSameUrlAndGetAnchor, handleCmsJump } from '../../tools/cms-link';
import { useRouter } from 'next/router';
import { useCroppedImage } from '@weee-fe/module';
import { parseJson } from '@/utils/parseJson';
import LazyLoad from '@/components/common/LazyLoad';

const Banner: React.FC<CmsComponentProps> = props => {
  let {
    data: info,
    properties,
    componentPos,
    trackingContext,
    isStatic,
    postUrl,
    componentDataSourceKey,
    cmsComponentKey
  } = props;
  const router = useRouter();
  const { bannerImpression } = useImpressionT2();
  const { bannerClick } = useClickActionT2();
  const { getCroppedUrl } = useCroppedImage();
  const [data, setData] = useState(info);
  const [load, setLoad] = useState(true);

  const ratio = useMemo(() => {
    const mobileImageInfo = parseJson(properties.mobileImageInfo);
    return mobileImageInfo ? `${mobileImageInfo.width} / ${mobileImageInfo.height}` : 'auto';
  }, [properties.mobileImageInfo]);

  const fetchData = async () => {
    const zipcode = getCookie(Constants.ZIP_CODE);
    const lang = getCookie(Constants.LANGUAGE);
    const date = getCookie(Constants.DELIVERY_DATE);
    const res = await getHomePageDataByUrl(postUrl, {
      zipcode,
      lang,
      date,
      dataobject_key: componentDataSourceKey
    });
    setLoad(false);
    if (res?.result) {
      setData(res.object);
    }
  };

  useEffect(() => {
    if (!isStatic) {
      fetchData();
    } else {
      setLoad(false);
    }
  }, []);

  if (load) {
    return (
      <div
        className="bg-surface-200-bg"
        style={{
          aspectRatio: ratio
        }}
      />
    );
  }
  if (data?.banner_list?.length) {
    return (
      <LazyLoad>
        <section
          data-module={props.cmsComponentKey}
          className={twMerge(
            classNames({
              'my-2.5': properties?.vertical_margin === Size.small,
              'my-5': properties?.vertical_margin === Size.middle,
              'my-10': properties?.vertical_margin === Size.large
            })
          )}>
          {data?.banner_list?.map((item, index) => {
            const bannerTrackParams = {
              mod_nm: properties?.event_key || cmsComponentKey,
              mod_pos: componentPos,
              co: {
                banner_pos: index,
                url: item?.link_url
              },
              ctx: trackingContext
            };
            const _className = twMerge(
              classNames('block text-center text-[0] p-0', {
                ['px-2']: properties?.horizontal_margin === ISize.small,
                ['px-3']: properties?.horizontal_margin === ISize.middle,
                ['px-5']: properties?.horizontal_margin === ISize.large
              })
            );
            let imageNode = (
              <img
                src={getCroppedUrl(data?.banner_list[0]?.img_url, { renderWidth: 375 })}
                className={twMerge(
                  classNames('cursor-pointer block', {
                    ['w-full']: properties?.width_type === ImgSize.max_width,
                    ['w-screen max-w-full']: properties?.width_type === ImgSize.original_img,
                    ['rounded-600']: properties?.rounded_corner === IStatus.yes
                  })
                )}
                style={{
                  aspectRatio: ratio
                }}
                alt={`weee banner component ${index} image`}
              />
            );
            return (
              <ImpTracker
                key={index}
                impTrackingT2={() => {
                  //如果有链接才上报曝光 by: tina
                  if (item?.link_url) {
                    bannerImpression(bannerTrackParams);
                  }
                }}
                clickTrackingT2={() => {
                  if (item?.link_url) {
                    bannerClick(bannerTrackParams);
                  }
                }}>
                {item.link_url ? (
                  <a
                    className={_className}
                    aria-label={`Banner item ${index}`}
                    onClick={() => {
                      const { isSameUrl, anchor } = checkIsSameUrlAndGetAnchor(item.link_url);
                      if (isSameUrl) {
                        if (!anchor) return;
                        const el = document.querySelector(
                          `[data-keepalive="cms-activity-page-${trackingContext.page_target}"] #${anchor}`
                        );
                        if (el) {
                          el.scrollIntoView({
                            behavior: 'smooth',
                            block: 'center',
                            inline: 'nearest'
                          });
                        }
                      } else {
                        handleCmsJump(item.link_url, router);
                      }
                    }}>
                    {imageNode}
                  </a>
                ) : (
                  <div className={_className}>{imageNode}</div>
                )}
              </ImpTracker>
            );
          })}
        </section>
      </LazyLoad>
    );
  }
  return null;
};

export default Banner;
